import { BarcodeFormat, BrowserMultiFormatReader } from "@zxing/browser";
import { useEffect, useRef, useState } from "react";
import { LIMIT_MESSAGES, STATUS, STATUS_CLASS, STATUS_TEXT } from "../../config/barcode-scanner-config";
import { isValidEAN13 } from "../../helpers";

const FAILURE_THRESHOLD = 5;

const BarcodeScanner = ({ onDetected, onClose, onSuccess }) => {
	const isMountedRef = useRef(true);
	const videoRef = useRef(null);
	const controlsRef = useRef(null);
	const hasDetectedRef = useRef(false);

	const resetTimeoutRef = useRef(null);
	const successTimeoutRef = useRef(null);

	const failureCountsRef = useRef({
		[STATUS.INVALID]: 0,
		[STATUS.NOT_FOUND]: 0,
		[STATUS.FETCH_ERROR]: 0,
	});

	const [status, setStatus] = useState(STATUS.INIT);

	const [showLimitMessage, setShowLimitMessage] = useState(false);
	const [limitMessage, setLimitMessage] = useState("");

	// HELPERS

	const clearTimers = () => {
		clearTimeout(resetTimeoutRef.current);
		clearTimeout(successTimeoutRef.current);
	};

	const resetFailureCounts = () => {
		Object.keys(failureCountsRef.current).forEach((key) => {
			failureCountsRef.current[key] = 0;
		});
	};

	// TEMP STATUS
	const setTempStatus = (next) => {
		clearTimeout(resetTimeoutRef.current);

		setStatus(next);

		resetTimeoutRef.current = setTimeout(() => {
			if (!isMountedRef.current) return;

			hasDetectedRef.current = false;
			setStatus(STATUS.READY);
		}, 1200);
	};

	// HANDLE RESULT
	const handleScanResult = async (code) => {
		if (!isValidEAN13(code)) {
			handleScanFailure(STATUS.INVALID);
			return;
		}

		hasDetectedRef.current = true;

		setStatus(STATUS.LOADING);

		const res = await onDetected(code);
		if (!isMountedRef.current) return;

		if (res === null) return handleScanFailure(STATUS.NOT_FOUND);
		if (res === false) return handleScanFailure(STATUS.FETCH_ERROR);

		navigator.vibrate?.(50);
		setStatus(STATUS.SUCCESS);

		resetFailureCounts();
		successTimeoutRef.current = setTimeout(() => {
			if (!isMountedRef.current) return;
			onSuccess(res);
		}, 800);
	};

	const handleScanFailure = (status) => {
		failureCountsRef.current[status] += 1;

		Object.keys(failureCountsRef.current).forEach((key) => {
			if (key !== status) failureCountsRef.current[key] = 0;
		});

		// THRESHOLD REACHED
		if (failureCountsRef.current[status] >= FAILURE_THRESHOLD) {
			setLimitMessage(LIMIT_MESSAGES[status]);
			setShowLimitMessage(true);

			setTimeout(() => {
				if (!isMountedRef.current) return;

				setShowLimitMessage(false);
				onClose();
			}, 2000);

			return;
		}

		setTempStatus(status);
	};

	useEffect(() => {
		document.body.classList.add("hide-chat-widget");

		const reader = new BrowserMultiFormatReader();

		reader.hints.set("POSSIBLE_FORMATS", [BarcodeFormat.EAN_13]);

		const startScanner = async () => {
			controlsRef.current?.stop();

			try {
				setStatus(STATUS.STARTING);

				const controls = await reader.decodeFromVideoDevice(
					null,
					videoRef.current,
					async (result, err) => {
						if (result && !hasDetectedRef.current) {
							await handleScanResult(result.getText());
							return;
						}

						if (err && err.name !== "NotFoundException" && !hasDetectedRef.current) {
							console.warn(err);
							setTempStatus(STATUS.SCAN_ERROR);
						}
					},
				);

				controlsRef.current = controls;
				setStatus(STATUS.READY);
			} catch (err) {
				console.error(err);
				setStatus(STATUS.ERROR);
			}
		};

		startScanner();

		return () => {
			isMountedRef.current = false;
			controlsRef.current?.stop();
			document.body.classList.remove("hide-chat-widget");
			clearTimers();
		};
	}, []);

	return (
		<div className="barcode-overlay">
			<div className="barcode-container">
				<video
					ref={videoRef}
					className={`scanner-view ${showLimitMessage ? "has-limit-message" : ""}`}
					autoPlay
					playsInline
					muted
				/>

				{showLimitMessage ? (
					<div className="barcode-limit-message">
						<p>{limitMessage}</p>
					</div>
				) : (
					<>
						<div className={`scanner-status ${STATUS_CLASS[status]}`}>{STATUS_TEXT[status]}</div>

						<div className="scan-box" />
					</>
				)}

				<button className="camera-close-btn" onClick={onClose}>
					Stäng
				</button>
			</div>
		</div>
	);
};

export default BarcodeScanner;
